import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { financeApi } from "@/lib/finance/finance-api";
import {
	useCreateCreditNote,
	useInvoice,
	useMarkPaid,
} from "@/lib/finance/finance-hooks";
import { fmtFCFA } from "@/lib/format";
import type { TagClass } from "@/lib/status";
import { apiError, Field, inputStyle } from "./_ui";

const STATUS_CLASS: Record<string, TagClass> = {
	ISSUED: "info",
	PARTIAL: "warning",
	PAID: "success",
	OVERDUE: "danger",
	CANCELLED: "neutral",
};

/** Fiche facture — détail, PDF, paiement manuel, avoir. */
export function AdminInvoiceDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { data: inv, isLoading } = useInvoice(id);
	const markPaid = useMarkPaid(id!);
	const createCn = useCreateCreditNote();
	const [msg, setMsg] = useState<{
		kind: "danger" | "info";
		text: string;
	} | null>(null);
	const [cnOpen, setCnOpen] = useState(false);
	const [cn, setCn] = useState({
		reason: "RETURN",
		reasonDetail: "",
		totalHt: 0,
		totalVat: 0,
		refundMethod: "VOUCHER",
	});

	if (isLoading || !inv)
		return (
			<Card>
				<CardBody>Chargement…</CardBody>
			</Card>
		);

	const due = inv.totalTtc - inv.amountPaid;

	const doMarkPaid = () => {
		setMsg(null);
		markPaid.mutate(due, {
			onSuccess: () => setMsg({ kind: "info", text: "Facture marquée payée." }),
			onError: (e) => setMsg({ kind: "danger", text: apiError(e) }),
		});
	};

	const submitCn = () => {
		setMsg(null);
		createCn.mutate(
			{
				invoiceId: inv.id,
				reason: cn.reason,
				reasonDetail: cn.reasonDetail || undefined,
				totalHt: cn.totalHt,
				totalVat: cn.totalVat,
				refundMethod: cn.refundMethod,
			},
			{
				onSuccess: () => {
					setCnOpen(false);
					setMsg({ kind: "info", text: "Avoir créé." });
					navigate("/finance/avoirs");
				},
				onError: (e) => setMsg({ kind: "danger", text: apiError(e) }),
			},
		);
	};

	return (
		<>
			<PageHead
				title={<span className="mono">{inv.invoiceNo}</span>}
				sub={`${inv.type} · ${inv.customerSnapshot.name}`}
				actions={
					<>
						<AdminButton
							variant="ghost"
							onClick={() => navigate("/finance/factures")}
						>
							<AdminIcon n="arrow" s={14} /> Retour
						</AdminButton>
						<AdminButton
							variant="ghost"
							onClick={() => financeApi.openInvoicePdf(inv.id, inv.invoiceNo)}
						>
							<AdminIcon n="download" s={14} /> PDF
						</AdminButton>
						{inv.type !== "PROFORMA" &&
							inv.status !== "PAID" &&
							inv.status !== "CANCELLED" && (
								<AdminButton
									variant="ghost"
									onClick={doMarkPaid}
									disabled={markPaid.isPending}
								>
									<AdminIcon n="check" s={14} /> Marquer payée
								</AdminButton>
							)}
						{inv.type !== "PROFORMA" && inv.status !== "CANCELLED" && (
							<AdminButton
								variant="primary"
								onClick={() => {
									setCn({
										...cn,
										totalHt: inv.totalHt,
										totalVat: inv.totalVat,
									});
									setCnOpen((v) => !v);
								}}
							>
								<AdminIcon n="refresh" s={14} /> Avoir
							</AdminButton>
						)}
					</>
				}
			/>

			{msg && (
				<AlertBanner
					variant={msg.kind === "danger" ? "danger" : "info"}
					title={msg.text}
				/>
			)}

			{cnOpen && (
				<Card style={{ marginBottom: 16 }}>
					<CardHead
						title="Créer un avoir (note de crédit)"
						sub="Référence la facture initiale · écriture inverse automatique"
					/>
					<CardBody>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
								gap: 10,
							}}
						>
							<Field label="Motif">
								<select
									style={inputStyle}
									value={cn.reason}
									onChange={(e) => setCn({ ...cn, reason: e.target.value })}
								>
									<option value="RETURN">Retour</option>
									<option value="GESTURE">Geste commercial</option>
									<option value="ERROR">Erreur</option>
									<option value="CANCELLATION">Annulation</option>
								</select>
							</Field>
							<Field label="Total HT">
								<input
									type="number"
									style={inputStyle}
									value={cn.totalHt}
									onChange={(e) =>
										setCn({ ...cn, totalHt: Number(e.target.value) })
									}
								/>
							</Field>
							<Field label="Total TVA">
								<input
									type="number"
									style={inputStyle}
									value={cn.totalVat}
									onChange={(e) =>
										setCn({ ...cn, totalVat: Number(e.target.value) })
									}
								/>
							</Field>
							<Field label="Remboursement">
								<select
									style={inputStyle}
									value={cn.refundMethod}
									onChange={(e) =>
										setCn({ ...cn, refundMethod: e.target.value })
									}
								>
									<option value="VOUCHER">Bon d'achat</option>
									<option value="CASH">Espèces</option>
									<option value="REVERSAL">Contre-passation</option>
								</select>
							</Field>
						</div>
						<div style={{ marginTop: 10 }}>
							<Field label="Détail (optionnel)">
								<input
									style={inputStyle}
									value={cn.reasonDetail}
									onChange={(e) =>
										setCn({ ...cn, reasonDetail: e.target.value })
									}
								/>
							</Field>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 8,
								marginTop: 12,
							}}
						>
							<AdminButton variant="ghost" onClick={() => setCnOpen(false)}>
								Annuler
							</AdminButton>
							<AdminButton
								variant="primary"
								onClick={submitCn}
								disabled={createCn.isPending}
							>
								Créer l'avoir
							</AdminButton>
						</div>
					</CardBody>
				</Card>
			)}

			<div className="kpi-grid">
				<div className="kpi">
					<div className="kpi-l">Statut</div>
					<div className="kpi-v" style={{ fontSize: 18 }}>
						<Tag variant={STATUS_CLASS[inv.status] ?? "neutral"}>
							{inv.status}
						</Tag>
					</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Total HT</div>
					<div className="kpi-v tn">{fmtFCFA(inv.totalHt)}</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">TVA</div>
					<div className="kpi-v tn">{fmtFCFA(inv.totalVat)}</div>
				</div>
				<div className="kpi accent">
					<div className="kpi-l">Total TTC</div>
					<div className="kpi-v tn">{fmtFCFA(inv.totalTtc)}</div>
					<div className="kpi-foot muted">Restant dû {fmtFCFA(due)}</div>
				</div>
			</div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
					gap: 16,
				}}
			>
				<Card>
					<CardHead title="Client" />
					<CardBody>
						<div style={{ display: "grid", gap: 6 }}>
							<div>
								<strong>{inv.customerSnapshot.name}</strong>
							</div>
							{inv.customerSnapshot.niu && (
								<div className="mono muted">
									NIU : {inv.customerSnapshot.niu}
								</div>
							)}
							{inv.customerSnapshot.address && (
								<div className="muted">{inv.customerSnapshot.address}</div>
							)}
							{inv.customerSnapshot.city && (
								<div className="muted">{inv.customerSnapshot.city}</div>
							)}
							<div className="muted">
								Émise le {inv.issuedAt}
								{inv.dueAt ? ` · échéance ${inv.dueAt}` : ""}
							</div>
							{inv.pdfHash && (
								<div
									className="mono muted"
									style={{ fontSize: 11, wordBreak: "break-all" }}
								>
									SHA-256 : {inv.pdfHash}
								</div>
							)}
						</div>
					</CardBody>
				</Card>

				<Card>
					<CardHead
						title="Comptabilisation"
						sub="Écriture liée (journal VTE)"
					/>
					<CardBody>
						{inv.journalEntryId ? (
							<button
								className="btn-ghost"
								style={{ justifyContent: "flex-start" }}
								onClick={() => navigate("/finance/journaux")}
							>
								<AdminIcon n="layout" s={14} /> Écriture{" "}
								{inv.journalEntryId.slice(0, 8)}…
							</button>
						) : (
							<span className="muted">
								Aucune écriture (proforma non comptabilisée).
							</span>
						)}
					</CardBody>
				</Card>
			</div>

			<Card style={{ marginTop: 16 }}>
				<CardHead title="Lignes" />
				<CardBody>
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>#</th>
									<th>Désignation</th>
									<th className="right">Qté</th>
									<th className="right">PU HT</th>
									<th className="right">Total HT</th>
									<th>TVA</th>
									<th className="right">TTC</th>
								</tr>
							</thead>
							<tbody>
								{(inv.lines ?? []).map((l) => (
									<tr key={l.id}>
										<td>{l.lineNo}</td>
										<td>{l.designation}</td>
										<td className="right tn">{l.quantity}</td>
										<td className="right tn">{fmtFCFA(l.unitPriceHt)}</td>
										<td className="right tn">{fmtFCFA(l.totalHt)}</td>
										<td>
											{(l.vatRate * 100).toFixed(2).replace(/\.00$/, "")}%
										</td>
										<td className="right tn">
											<strong>{fmtFCFA(l.totalTtc)}</strong>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</CardBody>
			</Card>
		</>
	);
}
